/**
 * The English catalogue (ICU MessageFormat). Every string the app shows lives
 * here; `{brand}` is filled from @sable/brand, never typed in. The codename
 * must never appear in this file (docs/NAMING.md §5).
 */
export const en = {
  "app.loading": "Loading…",
  "app.error": "Something went wrong. {message}",
  "app.retry": "Try again",
  "app.back": "Back",
  "app.save": "Save",
  "app.saving": "Saving…",
  "app.saved": "Saved",
  "app.cancel": "Cancel",
  "app.delete": "Delete",
  "app.close": "Close",
  "app.edit": "Edit",
  "app.done": "Done",
  "app.optional": "optional",
  "app.unit": "Unit",
  "app.none": "None",

  "welcome.title": "Your resale business, in one place.",
  "welcome.body": "Photograph a find, price it from real sold listings, track every sale and fee, and know what you actually made.",
  "welcome.signIn": "Sign in with email",
  "welcome.signUp": "Create an account with email",
  "welcome.google": "Continue with Google",
  "welcome.facebook": "Continue with Facebook",
  "welcome.or": "or",
  "welcome.failed": "Sign-in didn't complete. Please try again.",
  "welcome.failedReason": "Sign-in didn't complete: {reason}",

  "onboarding.title": "Set up your workspace",
  "onboarding.body": "A workspace holds your inventory, sales and settings. You can invite helpers to it later.",
  "onboarding.name": "Business name",
  "onboarding.namePlaceholder": "e.g. Second Chance Finds",
  "onboarding.country": "Country",
  "onboarding.currency": "Currency",
  "onboarding.timezone": "Time zone",
  "onboarding.units": "Measurements",
  "onboarding.imperial": "Inches and pounds",
  "onboarding.metric": "Centimeters and kilograms",
  "onboarding.create": "Create workspace",

  "nav.dashboard": "Dashboard",
  "nav.inventory": "Inventory",
  "nav.add": "Add item",
  "nav.sales": "Sales",
  "nav.finance": "Finance",
  "nav.settings": "Settings",
  "nav.signOut": "Sign out",
  "nav.workspaces": "Workspaces",
  "nav.newWorkspace": "New workspace",

  "status.draft": "Draft",
  "status.in_stock": "In stock",
  "status.listed": "Listed",
  "status.sold": "Sold",
  "status.shipped": "Shipped",
  "status.donated": "Donated",
  "status.lost": "Lost",
  "status.damaged": "Damaged",
  "status.active": "Active",
  "status.all": "All",

  "listingStatus.queued": "Queued",
  "listingStatus.active": "Live",
  "listingStatus.sold": "Sold",
  "listingStatus.delisted": "Delisted",
  "listingStatus.ended": "Ended",
  "listingStatus.error": "Needs attention",

  "condition.nwt": "New with tags",
  "condition.nwot": "New without tags",
  "condition.excellent": "Excellent",
  "condition.great": "Great",
  "condition.good": "Good",
  "condition.fair": "Fair",

  "audience.adult": "Adult",
  "audience.kids": "Kids",
  "audience.baby": "Baby",
  "audience.unisex_all": "All ages",

  "season.spring": "Spring",
  "season.summer": "Summer",
  "season.fall": "Fall",
  "season.winter": "Winter",
  "season.all_season": "All season",

  "slot.front": "Front",
  "slot.back": "Back",
  "slot.brand_tag": "Brand tag",
  "slot.price_tag": "Price tag",
  "slot.detail": "Detail",
  "slot.measurement": "Measurement",
  "slot.flaw": "Flaw",
  "slot.extra": "Extra",

  "cost.shipping_label": "Shipping label",
  "cost.packaging": "Packaging",
  "cost.platform_fee": "Marketplace fee",
  "cost.payment_processing": "Payment processing",
  "cost.mileage": "Mileage",
  "cost.laundering": "Cleaning",
  "cost.repair": "Repair",
  "cost.promotion": "Promotion",
  "cost.other": "Other",

  "packaging.poly": "Poly mailer",
  "packaging.bubble": "Bubble mailer",
  "packaging.other": "Box or other",
  "packaging.none": "No packaging",

  "inventory.title": "Inventory",
  "inventory.search": "Search title, brand or SKU",
  "inventory.empty": "Nothing here yet.",
  "inventory.emptyAll": "Add your first item to start tracking your stock.",
  "inventory.loadMore": "Load more",
  "inventory.archived": "Archived",
  "inventory.personal": "Personal",
  "inventory.sort": "Sort",
  "inventory.sort.newest": "Newest",
  "inventory.sort.oldest": "Oldest",
  "inventory.sort.updated": "Recently changed",
  "inventory.sort.price_high": "Price: high to low",
  "inventory.sort.price_low": "Price: low to high",
  "inventory.showArchived": "Show archived",
  "inventory.untitled": "Untitled item",
  "inventory.cost": "Cost {amount}",
  "inventory.view": "View",
  "inventory.view.list": "List",
  "inventory.view.grid": "Grid",
  "inventory.listedOn": "Listed on {names}",
  "inventory.kpi.listed": "Active listings",
  "inventory.kpi.inStock": "{count, plural, one {# item} other {# items}} in stock",
  "inventory.kpi.sold": "Sold in {month}",
  "inventory.kpi.vsLast": "{change} vs {range}",
  "inventory.kpi.noneLast": "None sold {range}",
  "inventory.kpi.profit": "{month} profit",
  "inventory.kpi.afterCosts": "after fees and COGS",

  "dashboard.title": "Dashboard",
  "dashboard.seeAll": "See all",
  "dashboard.kpi.stockCost": "what {count, plural, one {# item} other {# items}} in stock cost",
  "dashboard.todo.title": "Needs attention",
  "dashboard.todo.clear": "All caught up. Nothing waiting on you.",
  "dashboard.todo.ship": "{count, plural, one {# sale} other {# sales}} to pack and ship",
  "dashboard.todo.unmatched": "{count, plural, one {# sale} other {# sales}} seen on a marketplace to match to an item",
  "dashboard.todo.drafts": "{count, plural, one {# draft} other {# drafts}} to finish",
  "dashboard.todo.unlisted": "{count, plural, one {# item} other {# items}} in stock but not listed",
  "dashboard.pipeline.title": "Stock by stage",
  "dashboard.pipeline.sellThrough": "Sell-through",
  "dashboard.pipeline.sellThroughHint": "sold in 12 months, of everything sold or still in stock",
  "dashboard.channel.units": "{count, plural, one {# item} other {# items}} sold",
  "dashboard.channel.tip": "{name}: {units} sold, {profit} profit",
  "dashboard.sales.toShip": "to ship",
  "dashboard.aging.title": "Listed longest",
  "dashboard.aging.days": "{count, plural, one {# day} other {# days}} in stock",
  "dashboard.aging.empty": "Nothing listed yet.",

  "intake.title": "Add an item",
  "intake.photos": "Photos",
  "intake.photosHint": "Take the front first. Tags help the price and brand reads.",
  "intake.addPhoto": "Add photo",
  "intake.details": "Details",
  "intake.titleField": "Title",
  "intake.titlePlaceholder": "What is it?",
  "intake.brand": "Brand",
  "intake.category": "Category",
  "intake.size": "Size",
  "intake.condition": "Condition",
  "intake.purchasePrice": "What you paid",
  "intake.tagPrice": "Tag price",
  "intake.source": "Where you found it",
  "intake.sourcePlaceholder": "Store, sale or market",
  "intake.bin": "Storage bin",
  "intake.itemWeight": "Item weight",
  "intake.weightHint": "Pounds, ounces, or both: 35 oz, 1.3 lbs, or 1 lb 10 oz",
  "intake.personal": "Keep for myself (not for resale)",
  "intake.save": "Save item",
  "intake.saveAnother": "Save and add another",
  "intake.uploading": "Uploading photos {done} of {total}…",
  "intake.photoFailed": "A photo didn't upload: {message}",

  "item.sku": "SKU {sku}",
  "item.photos": "Photos",
  "item.makePrimary": "Make cover photo",
  "item.primary": "Cover",
  "item.removePhoto": "Remove photo",
  "item.details": "Details",
  "item.description": "Description",
  "item.subcategory": "Subcategory",
  "item.audience": "Audience",
  "item.season": "Season",
  "item.colorway": "Color",
  "item.materials": "Materials",
  "item.materialsHint": "Comma separated",
  "item.conditionNotes": "Condition notes",
  "item.purchaseDate": "Bought on",
  "item.listings": "Listings",
  "item.noListings": "Not listed anywhere yet.",
  "item.addListing": "Add listing",
  "item.marketplace": "Marketplace",
  "item.listPrice": "List price",
  "item.listingUrl": "Listing link",
  "item.endListing": "End listing",
  "item.relist": "Relist",
  "item.markLive": "Mark live",
  "item.removeListing": "Remove",
  "item.sale": "Sale",
  "item.recordSale": "Record a sale",
  "item.salePrice": "Sale price",
  "item.buyerShipping": "Shipping the buyer paid",
  "item.soldOn": "Sold on",
  "item.orderId": "Order number",
  "item.packaging": "Packaging",
  "item.shippingLabel": "Postage you paid",
  "item.boosted": "Sold through a boosted listing",
  "item.markShipped": "Mark shipped",
  "item.markNotShipped": "Mark not shipped",
  "item.shippedOn": "Shipped {date}",
  "item.notShipped": "Not shipped yet",
  "item.tracking": "Tracking number",
  "item.markReturned": "Mark returned",
  "item.returned": "Returned",
  "item.undoSale": "Undo sale",
  "item.undoSaleConfirm": "Remove this sale and put the item back in stock?",
  "item.costs": "Costs",
  "item.addCost": "Add cost",
  "item.costKind": "Kind",
  "item.amount": "Amount",
  "item.note": "Note",
  "item.noCosts": "No extra costs.",
  "item.estimate": "estimate",
  "item.profit": "Profit",
  "item.revenue": "Revenue",
  "item.fees": "Marketplace fees",
  "item.cogs": "What you paid",
  "item.otherCosts": "Other costs",
  "item.net": "Net profit",
  "item.estimateAt": "At {price} on {marketplace}",
  "item.daysHeld": "{days, plural, one {# day} other {# days}} in stock",
  "item.daysToSale": "Sold in {days, plural, one {# day} other {# days}}",
  "item.pricing": "Pricing",
  "item.suggested": "Suggested {low} – {high}",
  "item.floor": "Floor {price}",
  "item.noPricing": "No price research yet.",
  "item.more": "More actions",
  "item.editDetails": "Edit details",
  "item.cover": "Cover photo",
  "item.gallery.public": "Public",
  "item.gallery.downloadThis": "Download this photo",
  "item.ai.running": "The AI is reading the photos and filling in the details. This takes about half a minute.",
  "item.ai.failed": "The AI couldn't fill in the details: {error}",
  "item.ai.done": "AI filled in {count, plural, one {# field} other {# fields}} on {when}; anything you typed is left as it is",
  "item.ai.check": "Check:",
  "item.ai.offer": "Let the AI read the photos and fill in the empty details",
  "item.ai.run": "Fill in with AI",
  "item.ai.again": "Run AI again",
  "item.carousel.title": "Photos",
  "item.carousel.open": "Open the photos full size",
  "item.carousel.previous": "Previous photo",
  "item.carousel.next": "Next photo",
  "item.gallery.downloadThisAs": "Download this photo as {format}",
  "item.gallery.downloadAll": "Download {count, plural, one {the photo} other {all # photos}} as",
  "item.gallery.downloadFailed": "The download didn't work: {message}",
  "item.gallery.publicHint": "Goes to marketplaces",
  "item.gallery.private": "Private",
  "item.gallery.privateHint": "Only you see these; never posted",
  "item.gallery.privateBadge": "Private · never posted",
  "item.gallery.addPublic": "Add a public photo",
  "item.gallery.addPrivate": "Add a private photo",
  "item.gallery.makePrivate": "Make private",
  "item.gallery.makePublic": "Make public",
  "item.gallery.noPublic": "No public photos: nothing can be posted",
  "posting.title": "Posting preview",
  "posting.hint": "Every field and value each marketplace would get, laid out like its own listing form",
  "posting.notBuilt": "Posting to {marketplace} isn't built yet. This is what the listing would carry.",
  "posting.empty": "Left empty",
  "posting.sentAs": "sent as {code}",
  "posting.onPost": "Matched when posting",
  "posting.notSent": "Not sent",
  "posting.photosOf": "{count} of {limit}",
  "posting.noPhotos": "No public photos",
  "posting.priceNote": "Prices are the ones a post would use now. A price typed in Marketplaces shows here once it's posted.",
  "posting.issues": "{count, plural, one {# to fix} other {# to fix}}",
  "posting.checks": "{count, plural, one {# to check} other {# to check}}",
  "item.photoOf": "{index} of {total}",
  "item.removePhotoConfirm": "Remove this photo?",
  "item.storage": "Storage & shipping",
  "item.missing": "Missing",
  "item.add": "Add",
  "item.marketplaces": "Marketplaces",
  "item.listingDetails": "Listing details",
  "item.listingDetailsHint": "What gets posted to each marketplace",
  "item.purchase": "Purchase",
  "item.showMore": "Show full description",
  "item.showLess": "Show less",
  "item.section.listing": "Listing",
  "item.section.purchase": "Purchase",
  "item.section.storage": "Storage",
  "item.estimatedProfit": "Estimated profit",
  "item.shipping": "Shipping",
  "item.suggestedPrice": "Suggested price",
  "item.bestOn": "Best on {marketplace}",
  "item.whyPrice": "Why this price",
  "item.legendLive": "Live prices",
  "item.legendSuggested": "Suggested",
  "item.legendFloor": "Break-even floor",
  "item.soldSummary": "Sold on {marketplace} for {price} · {date}",
  "item.ship.title": "Ship this order",
  "item.ship.titleDone": "Shipped",
  "item.ship.pick": "Pick it",
  "item.ship.fromBin": "From bin",
  "item.ship.noBin": "No bin recorded",
  "item.ship.pack": "Pack it",
  "item.ship.noPackaging": "No packaging recorded",
  "item.ship.label": "Buy the label",
  "item.ship.labelCost": "Label cost",
  "item.ship.trackingOptional": "Tracking number · optional",
  "item.ship.handOff": "Hand it off",
  "item.ship.handOffHint": "Mark it shipped once it's with the carrier.",
  "item.writeOff": "Write off",
  "item.writeOffReason": "Why is it leaving stock?",
  "item.restock": "Put back in stock",
  "item.archive": "Archive",
  "item.unarchive": "Unarchive",
  "item.deleteConfirm": "Delete this item, its photos and its history? This can't be undone.",
  "item.personal": "Personal item",
  "item.printLabel": "Print label",
  "item.measurements": "Measurements",
  "item.packagingDetails": "Packaging details",
  "item.packagingType": "Packaging type",
  "item.packagingSummary": "Packaging",
  "item.packageDims": "Package dimensions",
  "item.packageDimsHint": "Length × width × height; height is optional for a flat polybag",
  "item.length": "Length",
  "item.width": "Width",
  "item.height": "Height",
  "item.savePackaging": "Save packaging details",
  "packagingType.polybag": "Polybag",
  "packagingType.box": "Box",
  "item.measurementsTitle": "Measurements, laid flat",
  "item.measureAs": "Measure as",
  "item.saveMeasurements": "Save measurements",

  "garment.top": "Top",
  "garment.bottom": "Bottoms",

  "measure.pit_to_pit": "Pit to pit",
  "measure.pit_to_pit.hint": "Armpit to armpit",
  "measure.length.top": "Length",
  "measure.length.top.hint": "Top of the shoulder to the hem",
  "measure.length.bottom": "Length",
  "measure.length.bottom.hint": "Top of the waistband to the hem",
  "measure.sleeve": "Sleeve",
  "measure.sleeve.hint": "Shoulder seam to cuff",
  "measure.shoulder": "Shoulder",
  "measure.shoulder.hint": "Seam to seam across the back",
  "measure.waist": "Waist",
  "measure.waist.hint": "Across the top of the waistband",
  "measure.inseam": "Inseam",
  "measure.inseam.hint": "Crotch seam to hem",
  "measure.rise": "Rise",
  "measure.rise.hint": "Crotch seam to top of waistband",
  "measure.hip": "Hip",
  "measure.hip.hint": "Across the widest point",
  "measure.leg_opening": "Leg opening",
  "measure.leg_opening.hint": "Across the hem",

  "writeOff.damaged": "Damaged",
  "writeOff.donated": "Donated",
  "writeOff.lost": "Lost",

  "label.title": "Print a label",
  "label.printer": "Printer",
  "label.size": "Label size",
  "label.connect": "Connect printer",
  "label.connectSerial": "Connect by serial port",
  "label.print": "Print",
  "label.printed": "Sent to the printer.",
  "label.unsupported": "This browser can't reach a label printer. Use Chrome or Edge, or print from the phone app.",
  "label.copies": "Copies",

  "sales.title": "Sales",
  "sales.toShip": "To ship",
  "sales.recent": "Recent sales",
  "sales.empty": "No sales yet.",
  "sales.nothingToShip": "Nothing waiting to ship.",
  "sales.profit": "Profit {amount}",
  "sales.unmatched": "Seen on your marketplaces",
  "sales.unmatchedHint": "Your paired browser saw these sales but couldn't tie them to an item for certain. Record each on its item, then dismiss it here.",
  "sales.unmatchedReason.no_match": "No item matched",
  "sales.unmatchedReason.bundle": "Bundle: split it across its items",
  "sales.unmatchedReason.written_off": "The item was written off",
  "sales.dismiss": "Dismiss",

  "finance.title": "Finance",
  "finance.thisMonth": "This month",
  "finance.lastMonth": "Last month",
  "finance.ytd": "Year to date",
  "finance.last12": "Last 12 months",
  "finance.revenue": "Revenue",
  "finance.net": "Net profit",
  "finance.units": "Items sold",
  "finance.fees": "Fees",
  "finance.cogs": "Cost of goods",
  "finance.otherCosts": "Other costs",
  "finance.margin": "Margin",
  "finance.byMonth": "By month",
  "finance.byMarketplace": "By marketplace",
  "finance.inventory": "Inventory",
  "finance.activeUnits": "Items in stock",
  "finance.listedUnits": "Listed",
  "finance.capital": "Money in stock",
  "finance.includePersonal": "Include personal items",
  "finance.month": "Month",
  "finance.noSales": "No sales in this period.",

  "settings.title": "Settings",
  "settings.workspace": "Workspace",
  "settings.name": "Name",
  "settings.timezone": "Time zone",
  "settings.units": "Measurements",
  "settings.skuPrefix": "SKU prefix",
  "settings.skuPrefixHint": "Up to 8 capital letters, digits or dashes. New items only.",
  "settings.personalItems": "Track personal items",
  "settings.economics": "Costs and targets",
  "settings.economicsHint": "Used for profit estimates and floor prices.",
  "settings.polyMailer": "Poly mailer",
  "settings.bubbleMailer": "Bubble mailer",
  "settings.avgPackaging": "Other packaging",
  "settings.avgShipping": "Typical postage",
  "settings.buyerPaysShipping": "Buyers usually pay shipping",
  "settings.minProfit": "Minimum profit per item",
  "settings.targetMargin": "Target margin %",
  "settings.mileageRate": "Mileage rate",
  "settings.fees": "Marketplace fees",
  "settings.feesHint": "Starting values for your country. Change them if your account pays different rates.",
  "settings.commission": "Commission %",
  "settings.fixedFee": "Per order",
  "settings.paymentPct": "Payment %",
  "settings.paymentFixed": "Payment fixed",
  "settings.lowThreshold": "Low-price under",
  "settings.lowFee": "Low-price fee",
  "settings.boost": "Boost %",
  "settings.members": "People",
  "settings.role.owner": "Owner",
  "settings.role.admin": "Admin",
  "settings.role.member": "Member",
  "settings.role.helper": "Helper",
  "settings.account": "Account",
  "settings.signedInAs": "Signed in as {email}",

  "crosslist.account": "On @{handle}",
  "crosslist.accountId": "On account #{id}",
  "crosslist.postedFrom": "posted from {device}",
  "crosslist.title": "Crosslist",
  "crosslist.hint": "Post this item to other marketplaces from your own signed-in browser. When it sells anywhere, it comes down everywhere else.",
  "crosslist.post": "{count, plural, one {Post to # marketplace} other {Post to # marketplaces}}",
  "crosslist.posting": "Queueing…",
  "crosslist.liveSince": "live since {date}",
  "crosslist.viaExtension": "Posts and price changes go through your paired browser",
  "crosslist.pickHint": "Tick the marketplaces to post to or reprice",
  "crosslist.takedown.title": "Coming down everywhere else",
  "crosslist.takedown.hint": "The sale took this item off its other marketplaces. Check the ones below so it can't sell twice.",
  "crosslist.takedown.waiting": "{marketplace}: waiting for your paired browser to take it down",
  "crosslist.takedown.failed": "{marketplace}: couldn't take it down ({error}). Remove it there by hand.",
  "crosslist.takedown.byHand": "{marketplace}: take it down there by hand",
  "crosslist.preview": "Preview",
  "crosslist.hidePreview": "Hide preview",
  "crosslist.price": "Price",
  "crosslist.retry": "Post again",
  "crosslist.takeDown": "Take down",
  "crosslist.takeDownConfirm": "Take this listing down on {marketplace}?",
  "crosslist.cancel": "Cancel post",
  "crosslist.open": "Open on {marketplace}",
  "crosslist.recent": "Recent activity",
  "crosslist.noActivity": "Nothing queued yet.",
  "crosslist.photos": "{count, plural, one {# photo} other {# photos}}",
  "crosslist.warnings": "Check before posting",
  "crosslist.blockers": "Fix before posting",
  "crosslist.category": "Category",
  "crosslist.condition": "Condition",
  "crosslist.colors": "Colors",
  "crosslist.noTitle": "No title field: the description's first line shows",
  "crosslist.readiness.ready": "Ready: a paired browser is signed in",
  "crosslist.readiness.not_built": "Not available yet",
  "crosslist.readiness.off_in_country": "Not available in your country",
  "crosslist.readiness.listed_by_hand": "Already live there, listed outside {brand}",
  "crosslist.readiness.blocked": "Needs fixing first",
  "crosslist.readiness.no_device": "Will post once a paired browser is open",
  "crosslist.readiness.not_signed_in": "Will post once a paired browser is signed in there",
  "crosslist.readiness.connect_account": "Connect your account in Settings first",
  "crosslist.devices": "{count, plural, =0 {No browser paired. Pair one in Settings.} one {# browser paired} other {# browsers paired}}",
  "job.action.publish": "Post",
  "job.action.update_price": "Reprice",
  "job.action.delist": "Take down",
  "job.status.queued": "Waiting",
  "job.status.running": "Working",
  "job.status.succeeded": "Done",
  "job.status.failed": "Failed",
  "job.status.abandoned": "Skipped",
  "job.attempt": "attempt {count}",

  "sms.title": "Notifications",
  "sms.subtitle": "Text alerts",
  "sms.hint": "Get a text when something needs you now: an item that sold on one marketplace is still live on another, or a take-down failed. Alerts also appear in {brand} and by email.",
  "sms.phone": "Mobile number",
  "sms.phonePlaceholder": "(352) 555-0123",
  "sms.usOnly": "Enter a US mobile number.",
  "sms.terms": "Terms",
  "sms.privacy": "Privacy notice",
  "sms.sendCode": "Text me a code",
  "sms.sending": "Sending…",
  "sms.codeSent": "We texted a 6-digit code to {phone}. Enter it to start text alerts.",
  "sms.code": "Code",
  "sms.verify": "Confirm",
  "sms.resend": "Send another code",
  "sms.change": "Use a different number",
  "sms.on": "Text alerts go to {phone} (confirmed {when}).",
  "sms.turnOff": "Turn off texts",
  "sms.off": "Text alerts are off.",
  "extension.readShops": "Read your shops to bring every listing up to date →",
  "shops.nav": "Sync shops",
  "shops.title": "Your shops",
  "shops.hint": "A browser paired with {brand} reads each of your shops to the end and ties every listing to its item: the listing's id, link, price and status come across. Listings that sold there are taken down everywhere else.",
  "shops.readAll": "Read my shops",
  "shops.readOne": "Read",
  "shops.reading": "Reading…",
  "shops.never": "Not read yet",
  "shops.waiting": "Waiting for a paired browser signed in here (it checks in every minute)",
  "shops.running": "Reading the shop: {count, plural, one {# listing} other {# listings}} so far",
  "shops.failed": "The last reading failed: {error}",
  "shops.cancelled": "The last reading was cancelled",
  "shops.doneAt": "Read {when}",
  "shops.summary": "Read {when}: {seen, plural, one {# listing} other {# listings}}, {linked} tied to items, {updated} updated, {ended} ended, {review} to review",
  "shops.soldElsewhere": "{count, plural, one {# sold there and taken down elsewhere} other {# sold there and taken down elsewhere}}",
  "shops.incomplete": "not the whole shop, so nothing was ended",
  "shops.linkedCount": "{linked} of {total} open listings tied to the storefront",
  "shops.notSignedIn": "no online browser is signed in to {site}",
  "shops.noBrowser": "No paired browser is online. Readings wait until one checks in.",
  "shops.pairLink": "Pair a browser",
  "shops.noneReadable": "None of your marketplaces can be read from a browser in your country yet.",
  "shops.reviewTitle": "To review ({count})",
  "shops.reviewHint": "Live listings that couldn't be tied to an item for certain. Link each to its item, or ignore it; ignored listings aren't offered again.",
  "shops.reviewEmpty": "Nothing to review.",
  "shops.reason.no_match": "Not tied to an item",
  "shops.reason.duplicate": "A second live listing for an item that already has one here",
  "shops.reason.item_gone": "Still live, but its item already sold or was written off",
  "shops.suggestions": "Could be:",
  "shops.score": "{score}% alike",
  "shops.link": "This one",
  "shops.findItem": "Find the item",
  "shops.search": "Search your items",
  "shops.searchPlaceholder": "Title, brand or SKU",
  "shops.searchNone": "No items match.",
  "shops.ignore": "Ignore",
  "shops.takeDown": "Take it down",
  "shops.reportLink": "Check shops against inventory →",

  "shopReport.title": "Shop check",
  "shopReport.hint": "Where your shops, as the browser extension last read them, and your inventory disagree. Read your shops again to refresh this.",
  "shopReport.back": "← Sync shops",
  "shopReport.readAt": "Read {when}",
  "shopReport.partial": "partial reading: listings it didn't see aren't checked",
  "shopReport.notRead": "Not read yet: nothing to compare",
  "shopReport.seenCount": "{count, plural, one {# listing} other {# listings}}",
  "shopReport.all": "All",
  "shopReport.allMarketplaces": "Every shop",
  "shopReport.clean": "Your shops and your inventory agree.",
  "shopReport.emptyFilter": "Nothing of this kind.",
  "shopReport.storefront": "On {site}",
  "shopReport.ours": "In inventory",
  "shopReport.notShown": "Not shown in the shop",
  "shopReport.noItem": "No item",
  "shopReport.noListing": "No listing recorded there",
  "shopReport.account": "as @{handle}",
  "shopReport.openItem": "Open item",
  "shopReport.review": "Tie it to an item",
  "shopReport.openStorefront": "Open on {site}",
  "shopReport.seenStatus.active": "Live",
  "shopReport.seenStatus.sold": "Sold",
  "shopReport.seenStatus.inactive": "Hidden",
  "shopReport.kind.not_in_inventory": "Not in inventory",
  "shopReport.kind.sold_elsewhere": "Sold elsewhere, still live",
  "shopReport.kind.item_gone": "Item gone, still live",
  "shopReport.kind.sale_not_recorded": "Sale not recorded",
  "shopReport.kind.not_marked_listed": "Live, not marked listed",
  "shopReport.kind.not_live_there": "Marked live, isn't",
  "shopReport.kind.price_differs": "Price differs",
  "shopReport.explain.not_in_inventory": "Live on {site}, but no item in your inventory has this listing.",
  "shopReport.explain.sold_elsewhere": "Sold on {other}, but still live on {site}. Take it down there.",
  "shopReport.explain.item_gone": "Live on {site}, but its item is already sold or written off.",
  "shopReport.explain.sale_not_recorded": "Sold on {site}, but the item is still in stock here. Record the sale.",
  "shopReport.explain.not_marked_listed": "Live on {site}, but your inventory doesn't have it listed there.",
  "shopReport.explain.not_live_there": "Your inventory has it live on {site}, but the shop doesn't.",
  "shopReport.explain.price_differs": "Live on both, at different prices.",

  "extension.title": "Browser extension",
  "extension.hint": "The {brand} extension posts your listings and takes them down from a browser signed in to your marketplaces. Your marketplace passwords never leave that browser.",
  "extension.none": "No browsers paired yet.",
  "extension.name": "Name for this browser",
  "extension.namePlaceholder": "e.g. Office laptop",
  "extension.pair": "Pair this browser",
  "extension.pairing": "Pairing…",
  "extension.paired": "Paired. The extension will check in within a minute.",
  "extension.notDetected": "The extension isn't installed in this browser. Install it, then press Pair again, or paste this code into the extension's popup:",
  "extension.codeOnce": "This code is shown once and works for this workspace only.",
  "extension.unpair": "Unpair",
  "extension.unpairConfirm": "Unpair {name}? It stops posting for this workspace at once.",
  "extension.online": "Online",
  "extension.offline": "Last seen {when}",
  "extension.offlineShort": "Offline",
  "extension.never": "Never checked in",
  "extension.paused": "Paused",
  "extension.signedInTo": "Signed in to {sites}",
  "extension.signedInNone": "Not signed in to any marketplace",
  "extension.pairedBy": "Paired by {email}",

  "notFound.title": "Page not found",
  "notFound.home": "Go to inventory",
} as const;

export type MessageKey = keyof typeof en;
